import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { SaveProcessSubscriptionRequest } from '@6grain/masofa-monolith-api-client/common/model/saveProcessSubscriptionRequest';
import { QuartzScheduleFieldsComponent } from '@app/pages/admin/components/quartz-schedule-fields/quartz-schedule-fields';
import { AssignmentStartFormComponent } from '@app/pages/admin/orchestration-process-page/components/assignment-start-form/assignment-start-form';
import {
  assignmentStartApiError,
  assignmentStartFormFromJson,
  buildAssignmentStartParameters,
  defaultAssignmentStartForm,
} from '@app/pages/admin/assignment-start.util';
import { looksLikeSafeSatelliteSnapshot } from '@app/pages/admin/process-subscription-form.util';
import {
  SubscriptionFormMode,
  SubscriptionProcessOption,
  emptySubscriptionForm,
  isBlankParametersJson,
  subscriptionCopyForm,
  subscriptionFormFromItem,
  subscriptionParametersTemplate,
  subscriptionSafePreviewJson,
} from '@app/pages/admin/process-subscription-form.util';

@Component({
  selector: 'app-subscription-form-dialog',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    CheckboxComponent,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
    MsfSelect,
    QuartzScheduleFieldsComponent,
    AssignmentStartFormComponent,
  ],
  templateUrl: './subscription-form-dialog.html',
  styleUrl: './subscription-form-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionFormDialogComponent {
  private readonly service = inject(ProcessSubscriptionApiService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly startForm = viewChild(AssignmentStartFormComponent);

  readonly saved = output<ProcessSubscriptionResponse>();

  readonly visible = signal(false);
  readonly saving = signal(false);
  readonly scheduleValid = signal(true);
  readonly formError = signal('');
  readonly mode = signal<SubscriptionFormMode>('create');
  readonly processOptions = signal<SubscriptionProcessOption[]>([]);
  readonly name = signal('');
  readonly processDefinitionKey = signal('');
  readonly cronExpression = signal('0 0 6 * * ?');
  readonly timeZoneId = signal('UTC');
  readonly parameterSeedJson = signal('');
  readonly isActive = signal(true);
  readonly safePreviewJson = signal('');

  readonly headerKey = computed(() => {
    if (this.mode() === 'edit') {
      return 'Edit subscription';
    }
    return this.mode() === 'copy' ? 'Create copy' : 'Create subscription';
  });
  readonly processLocked = computed(() => this.mode() !== 'create');
  readonly showSafePreview = computed(() => this.mode() !== 'create' && !!this.safePreviewJson());
  readonly canSave = computed(() =>
    !!(this.name() ?? '').trim()
    && !!(this.processDefinitionKey() ?? '')
    && !!(this.cronExpression() ?? '').trim()
    && !!(this.timeZoneId() ?? '').trim()
    && this.scheduleValid()
    && !this.saving(),
  );

  private editingId: string | null = null;

  openCreate(processes: SubscriptionProcessOption[], processKey?: string): void {
    const selectedKey = processKey && processes.some(item => item.id === processKey)
      ? processKey
      : (processes[0]?.id ?? '');
    this.applyForm('create', null, emptySubscriptionForm(), '');
    this.processOptions.set(processes);
    this.processDefinitionKey.set(selectedKey);
    this.parameterSeedJson.set(subscriptionParametersTemplate(selectedKey));
    this.visible.set(true);
  }

  openEdit(processes: SubscriptionProcessOption[], item: ProcessSubscriptionResponse): void {
    this.applyForm('edit', item.id ?? null, subscriptionFormFromItem(item), item.safeInputJson);
    this.processOptions.set(processes);
    this.visible.set(true);
  }

  openCopy(processes: SubscriptionProcessOption[], item: ProcessSubscriptionResponse): void {
    const copyName = this.translate.instant('Copy of', {
      name: item.name?.trim() || '—',
    });
    this.applyForm('copy', null, subscriptionCopyForm(item, copyName), item.safeInputJson);
    this.processOptions.set(processes);
    this.visible.set(true);
  }

  close(): void {
    if (this.saving()) {
      return;
    }
    this.visible.set(false);
    this.formError.set('');
  }

  setName(value: string): void {
    this.name.set(value);
  }

  setProcessDefinitionKey(value: string): void {
    this.processDefinitionKey.set(value);
    if (this.mode() === 'create') {
      this.parameterSeedJson.set(subscriptionParametersTemplate(value));
    }
  }

  setCronExpression(value: string): void {
    this.cronExpression.set(value);
  }

  setTimeZoneId(value: string): void {
    this.timeZoneId.set(value);
  }

  setActive(value: boolean): void {
    this.isActive.set(value);
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.formError.set('');
    const parameters = this.resolveParameters();
    if (parameters === null) {
      return;
    }
    const request: SaveProcessSubscriptionRequest = {
      name: this.name().trim(),
      processDefinitionKey: this.processDefinitionKey(),
      cronExpression: this.cronExpression().trim(),
      timeZoneId: this.timeZoneId().trim(),
      isActive: this.isActive(),
    };
    if (parameters) {
      request.parameters = parameters;
    }
    this.saving.set(true);
    const operation = this.editingId
      ? this.service.processSubscriptionsSubscriptionIdPut(this.editingId, request)
      : this.service.processSubscriptionsPost(request);
    operation
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: response => {
          this.visible.set(false);
          this.saved.emit((response ?? {}) as ProcessSubscriptionResponse);
        },
        error: error => {
          this.formError.set(assignmentStartApiError(error, 'Failed to save process subscription'));
        },
      });
  }

  private resolveParameters(): Record<string, unknown> | undefined | null {
    const built = this.buildStartParameters();
    const omitUnrestoredDefaults = this.mode() === 'edit'
      && isBlankParametersJson(this.parameterSeedJson());
    if (omitUnrestoredDefaults && !built?.ok) {
      return undefined;
    }
    if (!built) {
      this.formError.set('Parameters are required.');
      return null;
    }
    if (!built.ok) {
      this.formError.set(built.error);
      return null;
    }
    if (omitUnrestoredDefaults) {
      const defaults = buildAssignmentStartParameters(
        this.processDefinitionKey(),
        defaultAssignmentStartForm(this.processDefinitionKey()),
      );
      if (defaults.ok && JSON.stringify(built.parameters) === JSON.stringify(defaults.parameters)) {
        return undefined;
      }
    }
    if (looksLikeSafeSatelliteSnapshot(built.parameters)) {
      this.formError.set('Sanitized preview cannot be saved as process parameters');
      return null;
    }
    return built.parameters;
  }

  private buildStartParameters() {
    return this.startForm()?.build()
      ?? buildAssignmentStartParameters(
        this.processDefinitionKey(),
        assignmentStartFormFromJson(this.processDefinitionKey(), this.parameterSeedJson()),
      );
  }

  private applyForm(
    mode: SubscriptionFormMode,
    editingId: string | null,
    form: ReturnType<typeof emptySubscriptionForm>,
    safeInputJson?: string | null,
  ): void {
    this.mode.set(mode);
    this.editingId = editingId;
    this.formError.set('');
    this.name.set(form.name);
    this.processDefinitionKey.set(form.processDefinitionKey);
    this.cronExpression.set(form.cronExpression);
    this.timeZoneId.set(form.timeZoneId);
    this.parameterSeedJson.set(form.parametersJson);
    this.isActive.set(form.isActive);
    this.safePreviewJson.set(subscriptionSafePreviewJson(safeInputJson));
  }
}
